import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'wouter';
import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type Article = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  imageUrl?: string | null;
  author?: string | null;
  isPublished?: boolean | null;
  publishedAt?: string | null;
};

export default function BlogList() {
  const { t, i18n } = useTranslation();
  const [, setLocation] = useLocation();

  const { data, isLoading, error } = useQuery<Article[]>({
    queryKey: ['/api/articles', 'published'],
    queryFn: async () => {
      const res = await fetch('/api/articles?published=true&limit=50');
      if (!res.ok) throw new Error('Failed to load blog posts');
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const formatDate = (date: string | null | undefined) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-ES', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Blog</h1>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {t('statistics.errorLoadingTrending', { defaultValue: 'Error cargando contenido' })}
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-64 rounded-lg bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(data || []).map(post => (
            <article key={post.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 relative overflow-hidden cursor-pointer"
                   onClick={() => setLocation(`/blog/${post.slug}`)}>
                {post.imageUrl ? (
                  <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full" />
                )}
              </div>
              <div className="p-4">
                <h3 className="text-xl font-semibold text-foreground mb-2 line-clamp-2 hover:text-primary transition-colors cursor-pointer"
                    onClick={() => setLocation(`/blog/${post.slug}`)}>
                  {post.title}
                </h3>
                {post.excerpt && (
                  <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{post.excerpt}</p>
                )}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(post.publishedAt || null)}</span>
                  </div>
                  {post.author && (
                    <span className="italic">{post.author}</span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}


